import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useGetRelatedFeatures } from '~/hooks/apis/promo/useGetRelatedFeatures';
import { useSearchPromosByToggle } from '~/hooks/apis/promo/useSearchPromosByToggle';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';
import useDebounced from '~/hooks/useDebounced';

import type {
  GetRelatedFeaturesPayload,
  GetRelatedFeaturesResponse,
  PromoMeta,
  SearchPromosByToggleQuery,
  SearchPromosByToggleResponse,
} from '~/services/PromoService';

import { mergeToggleSearchResults } from './mergeToggleSearchResults';

export type PromoByToggleSearchResults = SearchPromosByToggleResponse['result'];
export type PromoByToggleEntity = SearchPromosByToggleQuery['entity'];
type SearchMode = PromoByToggleEntity | 'promo-and-voucher';
type Toggle = SearchPromosByToggleQuery['toggle'];
type ToggleSearchScope = 'configured-toggle' | 'no-toggles-set' | 'ignore-toggles';
type ReservedQuerySet = 'NO_TOGGLES_SET' | 'IGNORE_TOGGLES';
type RelatedFeatureResult = GetRelatedFeaturesResponse['promos'][number];
export type ToggleSearchRelatedFeature = RelatedFeatureResult['relatedTo'][number]['type'];

export const toggleSearchRelatedFeatures = {
  promo: [
    { value: 'promo-display-config', label: 'Promo Display' },
    { value: 'marketing-router', label: 'Marketing Router' },
  ],
  voucher: [
    { value: 'voucher-display-config', label: 'Voucher Display V1' },
    { value: 'marketing-router', label: 'Marketing Router' },
    { value: 'voucher-auto-assignment-rules', label: 'Voucher auto-assignment' },
  ],
} as const satisfies Record<PromoByToggleEntity, ReadonlyArray<{ value: ToggleSearchRelatedFeature; label: string }>>;

/** The complete, presentation-independent value produced by a toggle lookup. */
export type PromoByToggleSearch = {
  entity: PromoByToggleEntity | 'all';
  results: Array<PromoByToggleSearchResult>;
};

/** `relatedTo` and `warnings` are only present when related features were requested. */
export type PromoByToggleSearchResult = PromoByToggleSearchResults[number] &
  Partial<Pick<RelatedFeatureResult, 'relatedTo' | 'warnings'>> & {
    entity: PromoByToggleEntity;
  };

type LockedNameProps =
  | { codeName?: never; voucherName?: never }
  | {
      /** Locks the lookup to this promo code name and hides the editable name and entity controls. */
      codeName: string;
      voucherName?: never;
    }
  | {
      /** Locks the lookup to this voucher name and hides the editable name and entity controls. */
      codeName?: never;
      voucherName: string;
    };

type Props = {
  promoMeta?: PromoMeta | null;
  includeExtendedResults: boolean;
  /** Prevents a lookup until the caller makes this control active. */
  isActive?: boolean;
  /** Uses a reserved server query such as `IGNORE_TOGGLES` instead of a name lookup. */
  querySet?: ReservedQuerySet;
  /** Forces the related-feature lookup and hides the optional checkbox. */
  includeRelatedFeaturesSet?: boolean;
  /** Fixes the entity and hides the entity/toggle selectors for lookup-only use. */
  modeSet?: PromoByToggleEntity;
  /** Fixes the toggle and hides the entity/toggle selectors for lookup-only use. */
  toggleSet?: Toggle;
  /** Receives each completed lookup, or undefined while the lookup is changed or disabled. */
  onSearchChange?: (search?: PromoByToggleSearch) => void;
} & LockedNameProps;

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to search by toggle.';
}

const NAME_QUERY_DEBOUNCE_MS = 300;
const MIN_NAME_QUERY_LENGTH = 2;
const RESERVED_SCOPE_QUERIES = {
  'no-toggles-set': 'NO_TOGGLES_SET',
  'ignore-toggles': 'IGNORE_TOGGLES',
} as const;
const RESERVED_QUERY_SET_SCOPES: Record<ReservedQuerySet, ToggleSearchScope> = {
  NO_TOGGLES_SET: 'no-toggles-set',
  IGNORE_TOGGLES: 'ignore-toggles',
};

function getSelectionLabel(scope: ToggleSearchScope, toggle?: Toggle): string {
  if (scope === 'configured-toggle') {
    return toggle ?? 'Select a toggle';
  }
  return scope === 'no-toggles-set' ? '[No toggles set]' : '[Ignore toggles]';
}

export default function PromoByToggle({
  promoMeta,
  includeExtendedResults,
  codeName,
  voucherName,
  isActive = true,
  querySet,
  includeRelatedFeaturesSet,
  modeSet,
  toggleSet,
  onSearchChange,
}: Props) {
  const { promoBrand: brand } = useCurrentPromoBrand();
  const [searchMode, setSearchMode] = useState<SearchMode>('promo');
  const [promoToggle, setPromoToggle] = useState<Toggle>();
  const [voucherToggle, setVoucherToggle] = useState<Toggle>();
  const [promoScope, setPromoScope] = useState<ToggleSearchScope>('no-toggles-set');
  const [voucherScope, setVoucherScope] = useState<ToggleSearchScope>('no-toggles-set');
  const [query, setQuery] = useState('');
  const [includeRelatedFeatures, setIncludeRelatedFeatures] = useState(false);
  const debouncedQuery = useDebounced(query.trim(), NAME_QUERY_DEBOUNCE_MS);
  const lockedName = codeName ?? voucherName;
  const isNameLocked = lockedName !== undefined;
  const lockedEntity = codeName !== undefined ? 'promo' : voucherName !== undefined ? 'voucher' : undefined;
  const selectedMode = lockedEntity ?? modeSet ?? searchMode;
  const isLookupOnly = isNameLocked || modeSet !== undefined || toggleSet !== undefined;
  const showToggleSelector = querySet === undefined && (!isLookupOnly || (isNameLocked && toggleSet === undefined));
  const selectedIncludeRelatedFeatures = includeRelatedFeaturesSet ?? includeRelatedFeatures;
  const lookupQuery = lockedName ?? debouncedQuery;
  const isNameQueryReady =
    lookupQuery.trim().length >= MIN_NAME_QUERY_LENGTH && (isNameLocked || query.trim() === debouncedQuery);
  const promoOptions = promoMeta?.promo_toggles;
  const voucherOptions = promoMeta?.voucher_toggles;
  const selectedPromoScope = querySet ? RESERVED_QUERY_SET_SCOPES[querySet] : promoScope;
  const selectedVoucherScope = querySet ? RESERVED_QUERY_SET_SCOPES[querySet] : voucherScope;
  const selectedPromoToggle =
    toggleSet ?? promoOptions?.find((option) => option.key === promoToggle)?.key ?? promoOptions?.[0]?.key;
  const selectedVoucherToggle =
    toggleSet ?? voucherOptions?.find((option) => option.key === voucherToggle)?.key ?? voucherOptions?.[0]?.key;
  const selectedScope = selectedMode === 'voucher' ? selectedVoucherScope : selectedPromoScope;
  const selectedToggle = selectedMode === 'voucher' ? selectedVoucherToggle : selectedPromoToggle;
  const selectValue =
    selectedMode === 'promo-and-voucher'
      ? selectedPromoScope === selectedVoucherScope && selectedPromoScope !== 'configured-toggle'
        ? selectedPromoScope
        : ''
      : selectedScope === 'configured-toggle'
        ? selectedToggle
          ? `${selectedMode}:${selectedToggle}`
          : ''
        : selectedScope;
  const hasSeparateSelections = selectedMode === 'promo-and-voucher' && selectValue === '';

  const buildRequest = useCallback(
    (entity: PromoByToggleEntity): SearchPromosByToggleQuery | null => {
      const scope = entity === 'promo' ? selectedPromoScope : selectedVoucherScope;
      if (!isActive || (scope === 'configured-toggle' && !isNameQueryReady)) {
        return null;
      }
      const selectedToggle = entity === 'promo' ? selectedPromoToggle : selectedVoucherToggle;
      if (scope === 'configured-toggle' && !selectedToggle) {
        return null;
      }
      const scopeQuery = scope === 'configured-toggle' ? lookupQuery : RESERVED_SCOPE_QUERIES[scope];
      return {
        entity,
        ...(scope === 'configured-toggle' ? { toggle: selectedToggle } : {}),
        query: scopeQuery,
        brand,
        ...(includeExtendedResults ? { limit: 50 } : {}),
      };
    },
    [
      brand,
      includeExtendedResults,
      isActive,
      isNameQueryReady,
      lookupQuery,
      selectedPromoScope,
      selectedPromoToggle,
      selectedVoucherScope,
      selectedVoucherToggle,
    ],
  );

  const promoRequest = useMemo<SearchPromosByToggleQuery | null>(
    () => (selectedMode === 'voucher' ? null : buildRequest('promo')),
    [buildRequest, selectedMode],
  );
  const voucherRequest = useMemo<SearchPromosByToggleQuery | null>(
    () => (selectedMode === 'promo' ? null : buildRequest('voucher')),
    [buildRequest, selectedMode],
  );
  const {
    data: promoData,
    error: promoError,
    isFetching: isFetchingPromos,
    refetch: refetchPromos,
  } = useSearchPromosByToggle(promoRequest);
  const {
    data: voucherData,
    error: voucherError,
    isFetching: isFetchingVouchers,
    refetch: refetchVouchers,
  } = useSearchPromosByToggle(voucherRequest);

  const nameFilter = query.trim().toLowerCase();
  const toggleResults = useMemo(() => {
    const results = mergeToggleSearchResults(
      Boolean(promoRequest),
      promoData?.result,
      Boolean(voucherRequest),
      voucherData?.result,
    );
    return results && nameFilter
      ? results.filter((result) => result.name.toLowerCase().startsWith(nameFilter))
      : results;
  }, [nameFilter, promoData, promoRequest, voucherData, voucherRequest]);

  // Related features are served by a separate endpoint, looked up for the ids the toggle search returned.
  const relatedFeaturesPayload = useMemo<GetRelatedFeaturesPayload | null>(() => {
    if (!selectedIncludeRelatedFeatures || !toggleResults?.length) {
      return null;
    }
    return {
      promoIds: toggleResults.flatMap((result) => (result.entity === 'promo' ? [result.id] : [])),
      voucherIds: toggleResults.flatMap((result) => (result.entity === 'voucher' ? [result.id] : [])),
      brand,
    };
  }, [brand, selectedIncludeRelatedFeatures, toggleResults]);
  const {
    data: relatedFeaturesData,
    error: relatedFeaturesError,
    isFetching: isFetchingRelatedFeatures,
    refetch: refetchRelatedFeatures,
  } = useGetRelatedFeatures(relatedFeaturesPayload);

  useEffect(() => {
    onSearchChange?.();
  }, [onSearchChange, promoRequest, voucherRequest, selectedIncludeRelatedFeatures]);

  useEffect(() => {
    // Wait for related features, but still show the toggle results if that lookup fails.
    if (!toggleResults || (relatedFeaturesPayload && !relatedFeaturesData && !relatedFeaturesError)) {
      return;
    }
    const relatedFeaturesById = new Map<string, RelatedFeatureResult>([
      ...(relatedFeaturesData?.promos ?? []).map((related) => [`promo:${related.id}`, related] as const),
      ...(relatedFeaturesData?.vouchers ?? []).map((related) => [`voucher:${related.id}`, related] as const),
    ]);
    onSearchChange?.({
      entity: selectedMode === 'promo-and-voucher' ? 'all' : selectedMode,
      results: toggleResults.map((result) => {
        const related = relatedFeaturesPayload ? relatedFeaturesById.get(`${result.entity}:${result.id}`) : undefined;
        return related ? { ...result, relatedTo: related.relatedTo, warnings: related.warnings } : result;
      }),
    });
  }, [onSearchChange, relatedFeaturesData, relatedFeaturesError, relatedFeaturesPayload, selectedMode, toggleResults]);

  function handleSelectionChange(value: string) {
    if (value === 'no-toggles-set' || value === 'ignore-toggles') {
      if (selectedMode !== 'voucher') {
        setPromoScope(value);
      }
      if (selectedMode !== 'promo') {
        setVoucherScope(value);
      }
      return;
    }
    const promoOption = promoOptions?.find((option) => `promo:${option.key}` === value);
    if (promoOption) {
      setPromoToggle(promoOption.key);
      setPromoScope('configured-toggle');
      setSearchMode('promo');
      return;
    }
    const voucherOption = voucherOptions?.find((option) => `voucher:${option.key}` === value);
    if (voucherOption) {
      setVoucherToggle(voucherOption.key);
      setVoucherScope('configured-toggle');
      setSearchMode('voucher');
    }
  }

  function handleSearchAgain() {
    if (promoRequest) {
      void refetchPromos();
    }
    if (voucherRequest) {
      void refetchVouchers();
    }
    if (relatedFeaturesPayload) {
      void refetchRelatedFeatures();
    }
  }

  return (
    <Box mt={4}>
      <Stack spacing={2}>
        {!isLookupOnly && (
          <Box>
            <Typography variant="h6">Promo and voucher toggle search</Typography>
            <Typography variant="body2" color="text.secondary">
              Select a toggle and enter at least two characters to search by name. For bracketed scopes, the name
              filters loaded results.
            </Typography>
            <RadioGroup
              row
              aria-label="Search type"
              value={selectedMode}
              onChange={(event) => setSearchMode(event.target.value as SearchMode)}
            >
              <FormControlLabel value="promo" control={<Radio />} label="Promo" />
              <FormControlLabel value="voucher" control={<Radio />} label="Voucher" />
              <FormControlLabel value="promo-and-voucher" control={<Radio />} label="Promo and voucher" />
            </RadioGroup>
          </Box>
        )}
        <Grid container spacing={2}>
          {showToggleSelector && (
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Toggle or scope"
                value={selectValue}
                onChange={(event) => handleSelectionChange(event.target.value)}
                helperText={
                  hasSeparateSelections
                    ? `Promo: ${getSelectionLabel(selectedPromoScope, selectedPromoToggle)}; Voucher: ${getSelectionLabel(selectedVoucherScope, selectedVoucherToggle)}`
                    : undefined
                }
                slotProps={{ select: { displayEmpty: true, MenuProps: { variant: 'menu' } } }}
                fullWidth
              >
                <MenuItem value="" disabled>
                  {hasSeparateSelections ? 'Separate selections' : 'Select a toggle or scope'}
                </MenuItem>
                <MenuItem disabled>Promo</MenuItem>
                {promoOptions?.map((option: { key: Toggle }) => (
                  <MenuItem key={`promo:${option.key}`} value={`promo:${option.key}`}>
                    {option.key}
                  </MenuItem>
                ))}
                <MenuItem disabled>Voucher</MenuItem>
                {voucherOptions?.map((option: { key: Toggle }) => (
                  <MenuItem key={`voucher:${option.key}`} value={`voucher:${option.key}`}>
                    {option.key}
                  </MenuItem>
                ))}
                <MenuItem value="no-toggles-set">[No toggles set]</MenuItem>
                <MenuItem value="ignore-toggles">[Ignore toggles]</MenuItem>
              </TextField>
            </Grid>
          )}
          {!isNameLocked && (
            <Grid size={{ xs: 12, md: isLookupOnly ? 10 : selectedMode === 'promo-and-voucher' ? 4 : 7 }}>
              <TextField
                label="Name query"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                fullWidth
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 2 }} display="flex" alignItems="center">
            <Button
              variant="contained"
              disabled={(!promoRequest && !voucherRequest) || isFetchingPromos || isFetchingVouchers}
              onClick={handleSearchAgain}
            >
              Search
            </Button>
          </Grid>
          {includeRelatedFeaturesSet === undefined && (
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedIncludeRelatedFeatures}
                    onChange={(event) => setIncludeRelatedFeatures(event.target.checked)}
                  />
                }
                label="Include related features"
              />
            </Grid>
          )}
        </Grid>
        {(isFetchingPromos || isFetchingVouchers || isFetchingRelatedFeatures) && (
          <CircularProgress size={24} aria-label="Searching promos and vouchers" />
        )}
        {(promoError || voucherError || relatedFeaturesError) && (
          <Alert severity="error">{getErrorMessage(promoError ?? voucherError ?? relatedFeaturesError)}</Alert>
        )}
      </Stack>
    </Box>
  );
}
